const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256_RE = /^[a-f0-9]{64}$/;
const ASCII_WHITESPACE_RE = /[ \t\n\r\f\v]+/g;
const ASCII_EDGE_WHITESPACE_RE = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;
const APPOINTMENT_KEYS = [
  'client_id', 'consultation_ordinal', 'period_label', 'practitioner_note',
  'questions', 'review_content_sha256', 'reviewed_by_anissa', 'source_consultation_id',
  'summary', 'version',
].sort();
const CYCLE_APPOINTMENT_KEYS = [...APPOINTMENT_KEYS, 'occurred_confirmed', 'cycle_review'].sort();
const CYCLE_REVIEW_KEYS = ['id', 'step_number', 'template_type'];
const CYCLE_REVIEW_TEMPLATES = new Set(['adherence', 'results', 'metabolic', 'final']);
const QUESTION_KEYS = ['answer', 'id', 'source', 'text'];

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function hasExactKeys(value, expected) {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return keys.length === sortedExpected.length && keys.every((key, index) => key === sortedExpected[index]);
}

function unicodeLength(value) {
  return Array.from(value).length;
}

function isCanonicalSingleLine(value, maxLength, required = false) {
  if (typeof value !== 'string' || unicodeLength(value) > maxLength) return false;
  const canonical = value.replace(ASCII_WHITESPACE_RE, ' ').replace(/^ +| +$/g, '');
  return value === canonical && (!required || value.length > 0);
}

export function readExactAppointmentV2(value) {
  const hasCycleProof = isPlainObject(value)
    && (Object.hasOwn(value, 'occurred_confirmed') || Object.hasOwn(value, 'cycle_review'));
  const cycleReview = value?.cycle_review;
  if (!hasExactKeys(value, hasCycleProof ? CYCLE_APPOINTMENT_KEYS : APPOINTMENT_KEYS)
      || value.version !== 2
      || !UUID_RE.test(value.client_id)
      || !UUID_RE.test(value.source_consultation_id)
      || !Number.isInteger(value.consultation_ordinal)
      || value.consultation_ordinal < 1
      || value.consultation_ordinal > 6
      || value.reviewed_by_anissa !== true
      || !SHA256_RE.test(value.review_content_sha256)
      || !isCanonicalSingleLine(value.summary, 1600)
      || !isCanonicalSingleLine(value.period_label, 160)
      || typeof value.practitioner_note !== 'string'
      || unicodeLength(value.practitioner_note) > 2400
      || value.practitioner_note !== value.practitioner_note
        .replace(/\r\n/g, '\n')
        .replace(ASCII_EDGE_WHITESPACE_RE, '')
      || !Array.isArray(value.questions)
      || value.questions.length > 7
      || (hasCycleProof && (
        value.occurred_confirmed !== true
        || !hasExactKeys(cycleReview, CYCLE_REVIEW_KEYS)
        || !UUID_RE.test(cycleReview.id)
        || !Number.isInteger(cycleReview.step_number)
        || cycleReview.step_number < 1
        || cycleReview.step_number > 8
        || !CYCLE_REVIEW_TEMPLATES.has(cycleReview.template_type)
      ))) {
    return null;
  }
  const questions = value.questions.map((question) => {
    if (!hasExactKeys(question, QUESTION_KEYS)
        || !isCanonicalSingleLine(question.id, 100)
        || !isCanonicalSingleLine(question.text, 320, true)
        || (question.source !== 'ia' && question.source !== 'anissa')
        || !isCanonicalSingleLine(question.answer, 2400)) {
      return null;
    }
    return { text: question.text, answer: question.answer };
  });
  if (questions.some((question) => question === null)
      || (!value.summary && questions.length === 0)) {
    return null;
  }
  return {
    summary: value.summary,
    questions,
    sha256: value.review_content_sha256,
    cycleReview: hasCycleProof ? cycleReview : null,
  };
}
